import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { patchPreferredLanguage } from "@/api/meApi";
import { useAuth } from "@/hooks/useAuth";
import { cachePreferredLanguage } from "@/lib/setUiLanguage";
import { normalizeUiLanguage } from "@/lib/uiLanguage";

/**
 * UI language follows the signed-in person, not the company country.
 * Stored language wins on login; a first visit adopts the current browser language.
 */
export function PersonLanguageSync(): null {
  const { user, session } = useAuth();
  const { i18n } = useTranslation();
  const adoptedForUserRef = useRef<string | null>(null);
  const preferredLanguage = user?.preferredLanguage ?? null;
  const userId = user?.id ?? null;
  const sessionId = session?.id ?? null;
  const resolved = i18n.resolvedLanguage ?? i18n.language;

  useEffect(() => {
    if (!sessionId) {
      adoptedForUserRef.current = null;
    }
  }, [sessionId]);

  useEffect(() => {
    if (!userId) return;

    const current = normalizeUiLanguage(resolved);
    if (preferredLanguage) {
      if (current !== preferredLanguage) {
        void i18n.changeLanguage(preferredLanguage);
      }
      return;
    }

    if (adoptedForUserRef.current === userId) return;
    adoptedForUserRef.current = userId;
    void patchPreferredLanguage(current)
      .then((language) => {
        cachePreferredLanguage(language);
      })
      .catch(() => {
        adoptedForUserRef.current = null;
      });
  }, [userId, preferredLanguage, resolved, i18n]);

  return null;
}
